import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { useLang } from '@/hooks/use-lang'
import { useIsCompact, useHasHover } from '@/hooks/use-media-query'
import { useCopy } from '@/hooks/use-copy'
import { t } from '@/lib/i18n'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ProviderMark } from '@/components/provider-mark'
import { Select } from '@/components/select'
import { inputStyle } from '@/lib/ui'
import { RULE_TYPES, RULE_TYPE_LABELS, CONDITION_OPTIONS, CONDITION_LABELS } from '@/lib/transforms'
import type { Endpoint, Tenant, TransformRule, RoutingTarget } from '@/lib/types'

interface EndpointStat {
  endpoint_id: string
  total: number
  success_rate: number
  rate_per_hour: number
  sparkline?: number[]
}

/*
  EndpointSpark — tiny SVG mini-bar chart shown next to the rate label
  on each endpoint row. 24 buckets over the analytics period; all-zero
  arrays render nothing so freshly-created endpoints don't show a flat
  baseline.
*/
function EndpointSpark({ values, success }: { values: number[]; success: number }) {
  const max = Math.max(...values, 1)
  const w = 84
  const h = 22
  const gap = 1
  const barW = (w - gap * (values.length - 1)) / values.length
  const isErrHeavy = success < 95
  const fill = isErrHeavy ? 'var(--red)' : 'var(--green)'
  if (max === 0) return null
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', opacity: 0.85 }}>
      {values.map((v, i) => {
        const barH = (v / max) * h
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={h - barH}
            width={barW}
            height={Math.max(1, barH)}
            fill={fill}
            rx={0.5}
          />
        )
      })}
    </svg>
  )
}

/*
  formatRate — pick a unit that makes the number readable. /h reads as
  "this thing fires often"; for sleepy endpoints we fall back to the
  absolute period total so we don't show a misleading "0/h" on rows
  that actually had a few events. Tuned for the prototype's information
  density (`1.2k/h`, `120/h`, `5/7d`).
*/
function formatRate(perHour: number, total: number): string {
  if (perHour >= 1000) return `${(perHour / 1000).toFixed(1)}k/h`
  if (perHour >= 1) return `${Math.round(perHour)}/h`
  return `${total}/7d`
}

// Tiny inline copy icon — no label, no border. Used in the
// always-visible slug line on each endpoint row.
function CopyIcon({ text, title }: { text: string; title: string }) {
  const { copied, copy } = useCopy()
  const hasHover = useHasHover()
  const handle = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    copy(text)
  }
  return (
    <button
      type="button"
      onClick={handle}
      title={copied ? (t('copied') || 'Copied') : title}
      aria-label={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Glyph stays 18px; on touch the hit area grows to >=44px so the
        // copy target is reachable without a precise pointer.
        width: hasHover ? 18 : 44, height: hasHover ? 18 : 44,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: copied ? 'var(--green)' : 'var(--muted)',
        opacity: 0.7,
        transition: 'opacity 120ms ease, color 120ms ease',
        padding: 0,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '1' }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7' }}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  )
}

function CopyButton({ text }: { text: string }) {
  const { copied, copy } = useCopy()

  return (
    <button
      onClick={() => copy(text)}
      title={t('copy')}
      className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-xs font-medium transition tap"
      style={copied
        ? { backgroundColor: 'var(--accent-soft)', color: 'var(--green)' }
        : { backgroundColor: 'var(--surface-2)', color: 'var(--muted)' }
      }
    >
      {copied ? (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {t('copied')}
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {t('copy')}
        </>
      )}
    </button>
  )
}

function SendTestButton({ endpointId }: { endpointId: string }) {
  const { token } = useAuth()
  const [state, setState] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle')
  const [statusCode, setStatusCode] = useState<number | null>(null)

  const handleSend = async () => {
    setState('sending')
    setStatusCode(null)
    try {
      const res = await api.post<{ status?: number }>(`/api/endpoints/${endpointId}/test`, {})
      setStatusCode(res?.status ?? 200)
      setState('ok')
    } catch {
      setState('error')
    }
    setTimeout(() => setState('idle'), 3000)
  }

  const label =
    state === 'sending' ? t('sending') :
    state === 'ok' ? t('sentStatus', { code: statusCode ?? '' }) :
    state === 'error' ? t('failedStatus', { code: statusCode ?? '' }) :
    t('sendTest')

  const btnStyle =
    state === 'ok' ? { backgroundColor: 'var(--accent-soft)', color: 'var(--green)' } :
    state === 'error' ? { backgroundColor: 'color-mix(in srgb, var(--red) 12%, transparent)', color: 'var(--red)' } :
    { backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }

  return (
    <button
      onClick={handleSend}
      disabled={state === 'sending'}
      className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition disabled:opacity-50 tap"
      style={btnStyle}
    >
      {state === 'sending' ? (
        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      ) : (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      )}
      {label}
    </button>
  )
}

// EndpointCard is the list item on /endpoints. Matches the Events row DA —
// 2-line density, status pill with animated dot, right-side hover actions —
// so the shell reads as a coherent product instead of one-page-per-style.
function EndpointCard({
  endpoint,
  proxyUrl,
  stat,
  onNavigate,
  onToggle,
  onDelete,
}: {
  endpoint: Endpoint
  proxyUrl: string
  stat?: EndpointStat
  onNavigate: () => void
  onToggle: () => void
  onDelete: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const compact = useIsCompact()
  const hasHover = useHasHover()
  const transformCount = endpoint.transform_rules?.length ?? 0
  const routeCount = endpoint.additional_targets?.length ?? 0
  const active = endpoint.is_active

  const statusColor = active ? 'var(--green)' : 'var(--muted)'
  const statusBg = active ? 'color-mix(in srgb, var(--green) 15%, transparent)' : 'var(--surface-2)'

  if (compact) {
    // Stacked layout for narrow viewports: the dense 7-col table reflows to a
    // single column with each datum on its own line, so nothing is clipped
    // and the touch targets stay reachable. Same product, stacked.
    return (
      <div
        onClick={onNavigate}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate() } }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          padding: '0.875rem 1rem',
          borderBottom: '1px solid var(--border)',
          cursor: 'pointer',
        }}
      >
        {/* Line 1: provider mark + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <ProviderMark name={endpoint.name} size={18} />
          <span style={{
            color: 'var(--text)',
            fontSize: '14px',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {endpoint.name}
          </span>
        </div>

        {/* Line 2: slug + copy */}
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: '11px',
          color: 'var(--muted)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            /wh/{endpoint.slug}
          </span>
          <CopyIcon text={proxyUrl} title={t('copyProxyUrl') || 'Copy proxy URL'} />
        </span>

        {/* Line 3: target URL (truncated) */}
        <div style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: '11.5px',
          color: 'var(--muted)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          <span aria-hidden="true" style={{ marginRight: 6 }}>→</span>{endpoint.target_url}
        </div>

        {/* Line 4: sparkline / rate / success + transform / route badges, wrapping */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 11, color: 'var(--muted)',
          }}>
            {stat && stat.total > 0 ? (
              <>
                {stat.sparkline && stat.sparkline.length > 0 && (
                  <EndpointSpark values={stat.sparkline} success={stat.success_rate} />
                )}
                <span style={{ textAlign: 'right' }}>
                  {formatRate(stat.rate_per_hour, stat.total)}
                </span>
                <span style={{
                  color: stat.success_rate >= 99 ? 'var(--green)'
                    : stat.success_rate >= 95 ? 'var(--amber)' : 'var(--red)',
                  textAlign: 'right',
                }}>{stat.success_rate.toFixed(1)}%</span>
              </>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {transformCount > 0 && (
              <Badge icon="τ" label={String(transformCount)} color="var(--accent-bright)" />
            )}
            {routeCount > 0 && (
              <Badge icon="⇉" label={String(routeCount)} color="var(--accent)" />
            )}
          </div>
        </div>

        {/* Line 5: status pill + actions */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <button
            onClick={(e) => { e.stopPropagation(); onToggle() }}
            title={active ? t('clickToDeactivate') : t('clickToActivate')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
              padding: '0 9px', borderRadius: '9999px',
              backgroundColor: statusBg, color: statusColor,
              fontSize: '11px', fontWeight: 500,
              border: 'none', cursor: 'pointer',
              justifyContent: 'center',
              minHeight: 44,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '0.4375rem', height: '0.4375rem', borderRadius: '9999px',
                backgroundColor: statusColor,
                animation: active ? 'ping-soft 2s ease-in-out infinite' : undefined,
              }}
            />
            {active ? t('active') : t('inactive')}
          </button>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}
            onClick={(e) => e.stopPropagation()}
          >
            <CopyButton text={proxyUrl} />
            <SendTestButton endpointId={endpoint.id} />
            <IconButton
              onClick={onDelete}
              title={t('delete')}
              color="var(--red)"
              visible
              touch
              icon={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                </svg>
              }
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onNavigate}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate() } }}
      style={{
        // Dense single-line grid matching the prototype's endpoints view:
        // provider mark · name+slug · → target URL · badges · status · actions.
        // Single-row layout reads as a table; the badges and actions sit
        // inline rather than wrapping to a second line.
        display: 'grid',
        gridTemplateColumns: '18px minmax(180px, 1.1fr) minmax(0, 1.5fr) auto auto auto auto',
        alignItems: 'center',
        columnGap: '0.75rem',
        padding: '0.75rem 1.25rem',
        borderBottom: '1px solid var(--border)',
        backgroundColor: hovered ? 'var(--surface)' : 'transparent',
        transition: 'background-color 120ms ease',
        cursor: 'pointer',
      }}
    >
      {/* Provider mark — initial-letter chip, deterministic hue per provider.
          Endpoints carry a name (e.g. "Stripe webhooks") rather than an
          explicit provider tag, so we use the name's first letter. */}
      <ProviderMark name={endpoint.name} size={18} />

      {/* Name + slug */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{
          color: 'var(--text)',
          fontSize: '13px',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {endpoint.name}
        </span>
        {/* Slug + always-visible copy icon. Click → copies the full
            proxy URL (not just the slug) since that's what the user
            pastes into Stripe / GitHub / etc. Inline so it's there
            even without hovering the row. */}
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: '10.5px',
          color: 'var(--muted)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            /wh/{endpoint.slug}
          </span>
          <CopyIcon text={proxyUrl} title={t('copyProxyUrl') || 'Copy proxy URL'} />
        </span>
      </div>

      {/* Target URL — mono, with leading arrow */}
      <div style={{
        fontFamily: 'var(--font-mono, ui-monospace, monospace)',
        fontSize: '11.5px',
        color: 'var(--muted)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        <span aria-hidden="true" style={{ marginRight: 6 }}>→</span>{endpoint.target_url}
      </div>

      {/* Throughput sparkline + rate + success — last 24h. Mini-bars
          give each row at-a-glance shape; hidden when the endpoint has
          zero events so freshly-created rows don't show stale "0/h". */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: 'var(--font-mono, ui-monospace, monospace)',
        fontSize: 11, color: 'var(--muted)',
      }}>
        {stat && stat.total > 0 ? (
          <>
            {stat.sparkline && stat.sparkline.length > 0 && (
              <EndpointSpark values={stat.sparkline} success={stat.success_rate} />
            )}
            <span style={{ minWidth: 48, textAlign: 'right' }}>
              {formatRate(stat.rate_per_hour, stat.total)}
            </span>
            <span style={{
              color: stat.success_rate >= 99 ? 'var(--green)'
                : stat.success_rate >= 95 ? 'var(--amber)' : 'var(--red)',
              minWidth: 44, textAlign: 'right',
            }}>{stat.success_rate.toFixed(1)}%</span>
          </>
        ) : null}
      </div>

      {/* Transform / route badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {transformCount > 0 && (
          <Badge icon="τ" label={String(transformCount)} color="var(--accent-bright)" />
        )}
        {routeCount > 0 && (
          <Badge icon="⇉" label={String(routeCount)} color="var(--accent)" />
        )}
      </div>

      {/* Status pill — click toggles active. On touch the pill keeps a
          >=44px tap target; the glyph and label stay small. */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle() }}
        title={active ? t('clickToDeactivate') : t('clickToActivate')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
          padding: '2px 9px', borderRadius: '9999px',
          backgroundColor: statusBg, color: statusColor,
          fontSize: '11px', fontWeight: 500,
          border: 'none', cursor: 'pointer',
          minWidth: '5rem', justifyContent: 'center',
          ...(hasHover ? null : { minHeight: 44 }),
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '0.4375rem', height: '0.4375rem', borderRadius: '9999px',
            backgroundColor: statusColor,
            animation: active ? 'ping-soft 2s ease-in-out infinite' : undefined,
          }}
        />
        {active ? t('active') : t('inactive')}
      </button>

      {/* Row actions — always visible: copy proxy URL, send a test, delete. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <CopyButton text={proxyUrl} />
        <SendTestButton endpointId={endpoint.id} />
        <IconButton
          onClick={onDelete}
          title={t('delete')}
          color="var(--red)"
          visible
          touch={!hasHover}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          }
        />
      </div>
    </div>
  )
}

// Badge renders a compact pill with an icon + label in a semantic color.
// Single component keeps alpha-hex mixing consistent across the row.
function Badge({ icon, label, color }: { icon: string; label: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
        padding: '0.1rem 0.45rem',
        backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
        color,
        borderRadius: '0.3rem',
        fontSize: '0.7rem',
        fontWeight: 500,
      }}
    >
      <span aria-hidden="true" style={{ fontFamily: 'ui-monospace, monospace' }}>{icon}</span>
      {label}
    </span>
  )
}

// IconButton shows on hover of the row (opacity 0 → 1) so the row stays calm
// when idle but reveals actions when the user cares.
function IconButton({
  onClick, title, color, visible, icon, touch = false,
}: {
  onClick: () => void
  title: string
  color: string
  visible: boolean
  icon: React.ReactNode
  // On touch the 14px glyph keeps a >=44px hit area so it stays tappable.
  touch?: boolean
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title}
      aria-label={title}
      style={{
        background: 'transparent', border: 'none',
        padding: '0.25rem', borderRadius: '0.25rem',
        color: hover ? color : 'var(--muted)',
        cursor: 'pointer',
        opacity: visible || hover ? 1 : 0,
        transition: 'opacity 120ms ease, color 120ms ease',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        ...(touch ? { minWidth: 44, minHeight: 44 } : null),
      }}
    >
      {icon}
    </button>
  )
}

function TargetUrlInfo({ onSelect }: { onSelect: (url: string) => void }) {
  const [open, setOpen] = useState(false)
  const hasHover = useHasHover()

  const httpbinUrls = [
    {
      badge: '200',
      bgColor: 'var(--accent-soft)',
      textColor: 'var(--green)',
      url: 'https://httpbin.org/post',
      desc: t('httpbinReturns200'),
    },
    {
      badge: '500',
      bgColor: 'color-mix(in srgb, var(--red) 12%, transparent)',
      textColor: 'var(--red)',
      url: 'https://httpbin.org/status/500',
      desc: t('httpbinReturns500'),
    },
    {
      badge: '5s',
      bgColor: 'color-mix(in srgb, var(--amber) 12%, transparent)',
      textColor: 'var(--amber)',
      url: 'https://httpbin.org/delay/5',
      desc: t('httpbinDelay'),
    },
  ]

  return (
    <div className="absolute right-2 top-1/2 -translate-y-1/2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="rounded-full flex items-center justify-center transition text-xs font-semibold"
        style={{
          backgroundColor: 'var(--surface-2)', color: 'var(--muted)',
          // Glyph stays 20px; on touch the tap target grows to >=44px.
          width: hasHover ? 20 : 44, height: hasHover ? 20 : 44,
        }}
        title={t('testingTips')}
      >
        i
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-[min(20rem,calc(100vw-2rem))] rounded-xl p-4 text-xs" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold" style={{ color: 'var(--white)' }}>{t('testingUrls')}</span>
            <button
              onClick={() => setOpen(false)}
              aria-label={t('close')}
              className="inline-flex items-center justify-center leading-none"
              style={{
                color: 'var(--muted)',
                width: hasHover ? 'auto' : 44, height: hasHover ? 'auto' : 44,
              }}
            >&times;</button>
          </div>
          <p className="mb-3" style={{ color: 'var(--muted)' }}>
            {t('testingUrlsIntro', {
              provider: '\u0000',
            }).split('\u0000').map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <a href="https://httpbin.org" target="_blank" rel="noopener" style={{ color: 'var(--accent)' }}>httpbin.org</a>
                )}
              </span>
            ))}
          </p>
          <div className="space-y-2">
            {httpbinUrls.map(entry => (
              <div key={entry.url} className="flex items-start gap-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <span className="shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold"
                  style={{ backgroundColor: entry.bgColor, color: entry.textColor }}>
                  {entry.badge}
                </span>
                <div className="flex-1 min-w-0">
                  <code className="text-[11px] font-mono break-all" style={{ color: 'var(--text)' }}>{entry.url}</code>
                  <p className="mt-0.5" style={{ color: 'var(--muted)' }}>{entry.desc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { onSelect(entry.url); setOpen(false) }}
                  className="shrink-0 text-xs font-medium"
                  style={{ color: 'var(--accent)' }}
                >
                  {t('useThisUrl')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function EndpointsPage() {
  useLang()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [targetUrl, setTargetUrl] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [transformRules, setTransformRules] = useState<TransformRule[]>([])
  const [routingTargets, setRoutingTargets] = useState<RoutingTarget[]>([])
  const [pendingDelete, setPendingDelete] = useState<Endpoint | null>(null)

  const { data: endpoints = [], isLoading } = useQuery<Endpoint[]>({
    queryKey: ['endpoints'],
    queryFn: () => api.get<Endpoint[]>('/api/endpoints'),
  })

  // Per-endpoint analytics over the last 7 days — drives both the rate
  // label and the inline mini-bar sparkline on each row. 7d gives the
  // sparks day-level shape (24 buckets ≈ 7h each) instead of the dense
  // hourly clusters you'd see at 24h.
  const { data: endpointStats = [] } = useQuery<EndpointStat[]>({
    queryKey: ['analytics-endpoints', '7d'],
    queryFn: () => api.get('/api/analytics/endpoints?period=7d'),
  })
  const statsById: Record<string, EndpointStat> = {}
  for (const s of endpointStats) statsById[s.endpoint_id] = s

  const { data: tenant } = useQuery<Tenant>({
    queryKey: ['tenant'],
    queryFn: () => api.get<Tenant>('/api/tenant'),
    enabled: !!user,
  })

  const proxyBaseUrl = tenant?.proxy_base_url ?? `/wh/${tenant?.slug ?? 'tenant'}`

  const getProxyUrl = (endpointSlug: string) =>
    `${proxyBaseUrl}/${endpointSlug}`

  const create = useMutation({
    mutationFn: () => api.post('/api/endpoints', {
      name,
      slug,
      target_url: targetUrl,
      ...(transformRules.length > 0 && { transform_rules: transformRules }),
      ...(routingTargets.length > 0 && { additional_targets: routingTargets }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] })
      setShowCreate(false)
      setName(''); setSlug(''); setTargetUrl('')
      setShowAdvanced(false); setTransformRules([]); setRoutingTargets([])
    },
  })

  const toggle = useMutation({
    mutationFn: (ep: Endpoint) => api.patch(`/api/endpoints/${ep.id}`, { is_active: !ep.is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endpoints'] }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/endpoints/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endpoints'] }),
  })

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4" style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--surface)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--white)' }}>{t('endpoints')}</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{t('endpointsCount', { count: endpoints.length })}</p>
          </div>
          {(showCreate || endpoints.length > 0) && (
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition"
              style={showCreate
                ? { backgroundColor: 'var(--surface-2)', color: 'var(--text)' }
                : { backgroundColor: 'var(--accent)', color: 'var(--on-accent)' }
              }
            >
              {showCreate ? t('cancel') : t('newEndpoint')}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6" style={{ backgroundColor: 'var(--bg)' }}>
        {showCreate && (
          <form
            onSubmit={e => { e.preventDefault(); create.mutate() }}
            className="p-5 rounded-xl space-y-4"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>{t('name')}</label>
                <input value={name} onChange={e => setName(e.target.value)} required placeholder="Stripe Payments"
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
                  style={inputStyle}
                  onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
                  onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>{t('slug')}</label>
                <input value={slug} onChange={e => setSlug(e.target.value)} required placeholder="stripe-payments"
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono focus:outline-none transition"
                  style={inputStyle}
                  onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
                  onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>
                  {t('targetUrl')} <span style={{ fontWeight: 400, opacity: 0.7 }}>· {t('targetUrlOptional')}</span>
                </label>
                <div className="relative">
                  <input value={targetUrl} onChange={e => setTargetUrl(e.target.value)} placeholder="https://api.myapp.com/webhook"
                    className="w-full px-3 py-2 pr-8 rounded-lg text-sm focus:outline-none transition"
                    style={inputStyle}
                    onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
                    onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
                  />
                  <TargetUrlInfo onSelect={(url: string) => setTargetUrl(url)} />
                </div>
                <p className="text-[11px] mt-1" style={{ color: 'var(--muted)' }}>{t('targetUrlHint')}</p>
              </div>
            </div>

            {/* Advanced section */}
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={() => setShowAdvanced(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 transition text-sm font-medium"
                style={{ backgroundColor: 'var(--surface-2)', color: 'var(--text)' }}
              >
                <span>{t('advanced')}</span>
                <svg
                  className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  style={{ color: 'var(--muted)' }}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAdvanced && (
                <div className="p-4 space-y-5" style={{ borderTop: '1px solid var(--border)' }}>
                  {/* Transform Rules */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{t('transformRules')}</span>
                      <button
                        type="button"
                        onClick={() => setTransformRules(r => [...r, { type: 'set_field', path: '', value: '' }])}
                        className="text-xs font-medium"
                        style={{ color: 'var(--accent)' }}
                      >
                        + {t('addRule')}
                      </button>
                    </div>
                    {transformRules.length === 0 && (
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{t('noRulesYet')}</p>
                    )}
                    <div className="space-y-2">
                      {transformRules.map((rule, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <Select
                            size="xs"
                            value={rule.type}
                            onChange={e => setTransformRules(r => r.map((x, j) => j === i ? { ...x, type: e.target.value as TransformRule['type'] } : x))}
                          >
                            {RULE_TYPES.map(rt => (
                              <option key={rt} value={rt}>{t(RULE_TYPE_LABELS[rt])}</option>
                            ))}
                          </Select>
                          <input
                            value={rule.path}
                            onChange={e => setTransformRules(r => r.map((x, j) => j === i ? { ...x, path: e.target.value } : x))}
                            placeholder={t('rulePath')}
                            className="flex-1 px-2 py-1 rounded text-xs focus:outline-none font-mono"
                            style={inputStyle}
                          />
                          <input
                            value={rule.value}
                            onChange={e => setTransformRules(r => r.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                            placeholder={t('ruleValue')}
                            className="flex-1 px-2 py-1 rounded text-xs focus:outline-none"
                            style={inputStyle}
                          />
                          <button
                            type="button"
                            onClick={() => setTransformRules(r => r.filter((_, j) => j !== i))}
                            aria-label={t('delete')}
                            className="transition text-lg leading-none self-end sm:self-auto inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
                            style={{ color: 'var(--muted)' }}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Routing Targets */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{t('additionalTargets')}</span>
                      <button
                        type="button"
                        onClick={() => setRoutingTargets(r => [...r, { url: '', condition: 'all' }])}
                        className="text-xs font-medium"
                        style={{ color: 'var(--accent)' }}
                      >
                        + {t('addTarget')}
                      </button>
                    </div>
                    {routingTargets.length === 0 && (
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{t('noTargetsYet')}</p>
                    )}
                    <div className="space-y-2">
                      {routingTargets.map((target, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <input
                            value={target.url}
                            onChange={e => setRoutingTargets(r => r.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                            placeholder="https://..."
                            className="flex-1 px-2 py-1 rounded text-xs focus:outline-none font-mono"
                            style={inputStyle}
                          />
                          <Select
                            size="xs"
                            value={target.condition}
                            onChange={e => setRoutingTargets(r => r.map((x, j) => j === i ? { ...x, condition: e.target.value as RoutingTarget['condition'] } : x))}
                          >
                            {CONDITION_OPTIONS.map(c => (
                              <option key={c} value={c}>{t(CONDITION_LABELS[c])}</option>
                            ))}
                          </Select>
                          <button
                            type="button"
                            onClick={() => setRoutingTargets(r => r.filter((_, j) => j !== i))}
                            aria-label={t('delete')}
                            className="transition text-lg leading-none self-end sm:self-auto inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
                            style={{ color: 'var(--muted)' }}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button type="submit" disabled={create.isPending}
                className="px-4 py-2 text-[var(--on-accent)] rounded-lg text-sm font-medium disabled:opacity-50 transition"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                {create.isPending ? t('loading') : t('createEndpoint')}
              </button>
              {slug && (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  {t('proxyUrl')}: <span className="font-mono" style={{ color: 'var(--text)' }}>{getProxyUrl(slug)}</span>
                </p>
              )}
            </div>
          </form>
        )}

        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--surface)' }}>
          {isLoading ? (
            <div className="px-5 py-12 text-center text-sm" style={{ color: 'var(--muted)' }}>{t('loading')}</div>
          ) : endpoints.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--accent-soft)' }}>
                <svg className="w-8 h-8" style={{ color: 'var(--accent)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--white)' }}>{t('createFirstEndpoint')}</h3>
              <p className="text-xs max-w-xs mb-5" style={{ color: 'var(--muted)' }}>
                {t('createFirstDesc')}
              </p>
              <button
                onClick={() => setShowCreate(true)}
                className="px-4 py-2 text-[var(--on-accent)] rounded-lg text-sm font-medium transition"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                {t('newEndpoint')}
              </button>
            </div>
          ) : (
            <div>
              {endpoints.map(ep => (
                <EndpointCard
                  key={ep.id}
                  endpoint={ep}
                  proxyUrl={getProxyUrl(ep.slug)}
                  stat={statsById[ep.id]}
                  onNavigate={() => navigate(`/endpoints/${ep.id}`)}
                  onToggle={() => toggle.mutate(ep)}
                  onDelete={() => setPendingDelete(ep)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id)
          setPendingDelete(null)
        }}
        title={t('deleteEndpointQ', { name: pendingDelete?.name ?? '' })}
        description={t('deleteEndpointDesc', { slug: `/${pendingDelete?.slug ?? ''}` })}
        confirmLabel={t('deleteEndpoint')}
        confirmKind="danger"
        requireText={pendingDelete?.slug}
      />
    </div>
  )
}
